<script>
import { Interaction } from '@/placeholders/utils/dom'
import DDrComponent from '@/components/TransformBox'
import { EVENT_COMPONENT_UNSELECT } from '../event-enums'
import { getComponentRef, getComponentRefsById } from '@/placeholders/utils/ref'
import { getBoundingRect } from '@/components/TransformBox/helper'
import { registerSelectionActions } from './plugin-selection-actions'

/**
 * 畫布選擇插件
 *
 * 功能：
 * 1. 為編輯器增加批量選擇功能（框選多個控件）
 * 2. 可批量拖動改變所選控件位置
 * 3. 可批量縮放改變所選控件大小
 * 4. 點擊頁面任何區域可使已選中的控件取消選中
 * 5. 可攔截刪除功能，對已選中的控件進行批量刪除
 * 6. 可攔截複製功能，對已選中的控件進行批量複製
 */
export default {
  name: 'CanvasSelectionPlugin',
  props: {
    application: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      grid: [1, 1],
      selectedComponents: [],
      selectionTransform: { x: 0, y: 0, width: 0, height: 0, rotation: 0 },
      selectionActive: false,
      selectionHandler: ['tl', 'tr', 'br', 'bl'],
      selectionArea: { x: 0, y: 0, width: 0, height: 0 }
    }
  },
  watch: {
    selectionActive(value) {
      if (!value) {
        this.selectedComponents = []
        this.selectionTransform = { x: 0, y: 0, width: 0, rotation: 0 }
        this.componentRefs = []
      }
    }
  },

  mounted() {
    this.componentRefs = []
    const element = this.application.getEditorView().getWrapperElement()

    Interaction.init(element, {
      onStart: () => {
        this.handleSelectionStart()
      },
      onMove: (interaction) => {
        this.handleSelectionMove(interaction)
      },
      onEnd: () => {
        this.handleSelectionEnd()
      }
    })
    // 註冊選擇相關的鍵盤快捷鍵（刪除、複製等）
    registerSelectionActions(this)
  },
  methods: {
    /**
     * 獲取被選中區域完全包含的控件
     * 只有完全被包含在選中區域內的控件才會被選中
     */
    getContains() {
      const rect = this.selectionArea
      const controls = this.application.controls
      const crossComponents = controls.filter((item) => {
        const transform = getBoundingRect(item.transform)
        const isXContain = transform.left > rect.x && transform.right < rect.x + rect.width
        const isYContain = transform.top > rect.y && transform.bottom < rect.y + rect.height
        return isXContain && isYContain
      })
      this.selectedComponents = crossComponents.map((item) => ({
        id: item.id,
        ...item.transform
      }))
    },
    /**
     * 處理選擇開始事件
     * 取消當前選中狀態，準備新的選擇
     */
    handleSelectionStart() {
      this.eventbus.$emit(EVENT_COMPONENT_UNSELECT)
      this.selectionActive = false
    },
    /**
     * 處理選擇移動事件
     * @param {Object} interaction - 交互對象，包含 x, y, downClientX, downClientY
     */
    handleSelectionMove({ x, y, downClientX, downClientY }) {
      if (x < 0) {
        x = Math.abs(x)
        downClientX -= x
      }
      if (y < 0) {
        y = Math.abs(y)
        downClientY -= y
      }
      this.selectionArea = { x: downClientX, y: downClientY, width: x, height: y }
      this.getContains()
    },
    /**
     * 處理選擇結束事件
     * 根據選中數量決定是單選還是多選
     */
    handleSelectionEnd() {
      if (this.selectedComponents.length === 1) {
        this.application.handleSelect(
          this.application.controls.find((item) => item.id === this.selectedComponents[0].id)
        )
        this.selectedComponents = []
      } else if (this.selectedComponents.length > 1) {
        this.application.handleUnselect()
        this.createSelectionTransform()
      }
      this.selectionArea = { x: 0, y: 0, width: 0, height: 0 }
    },
    /**
     * 創建選中區域的變換框
     * 計算所有選中控件的邊界框，用於顯示批量操作框
     */
    createSelectionTransform() {
      const x = []
      const y = []
      const x1 = []
      const y1 = []
      const ids = []
      this.selectedComponents.forEach((item) => {
        ids.push(item.id)
        item = getBoundingRect(item)
        x.push(item.left)
        y.push(item.top)
        x1.push(item.right)
        y1.push(item.bottom)
      })
      const left = Math.min(...x)
      const top = Math.min(...y)
      const right = Math.max(...x1)
      const bottom = Math.max(...y1)
      this.selectionActive = true
      this.selectionTransform = {
        x: left,
        y: top,
        width: right - left,
        height: bottom - top,
        rotation: 0
      }
      this.getSelectedComponentRefs(ids, this.selectionTransform)
    },
    /**
     * 獲取選中控件的引用並計算相對位置百分比
     * @param {Array<string>} ids - 控件 ID 陣列
     * @param {Object} transform - 選中區域的變換信息 { x, y, width, height }
     */
    getSelectedComponentRefs(ids, { x, y, width, height }) {
      this.componentRefs = getComponentRefsById(ids).map((item) => {
        // 記錄控件在選區的位置和尺寸百分比，用於批量變換時保持相對位置
        item._xPercent = (item.transform.x - x) / width
        item._yPercent = (item.transform.y - y) / height
        item._wPercent = item.transform.width / width
        item._hPercent = item.transform.height / height
        return item
      })
    },
    /**
     * 更新選中控件的變換信息
     * 從控件引用中同步最新的位置和尺寸
     */
    getSelectedComponentTransform() {
      this.selectedComponents = this.selectedComponents.map((item) => {
        const t = getComponentRef(item.id).transform
        item.x = t.x
        item.y = t.y
        item.width = t.width
        item.height = t.height
        return item
      })
    },
    /**
     * 處理批量拖動事件
     * 根據選中區域的移動，同步更新所有選中控件的位置
     * @param {Event} e - 事件對象
     */
    handleSelectionDrag(e) {
      e.stopPropagation()
      const area = this.$refs.transformBox.transform
      this.componentRefs.forEach((item) => {
        item.transform.x = Math.round(area.x + area.width * item._xPercent)
        item.transform.y = Math.round(area.y + area.height * item._yPercent)
      })
      this.getSelectedComponentTransform()
    },
    /**
     * 阻止事件冒泡
     * @param {Event} e - 事件對象
     */
    stopPropagation(e) {
      e.stopPropagation()
    },
    /**
     * 處理批量拖動結束事件
     * 批量更新所有選中控件的變換信息
     * @param {Event} e - 事件對象
     * @param {Object} transform - 最終的變換信息
     */
    handleSelectionDragEnd(e, transform) {
      e.stopPropagation()
      const changes = {}
      this.componentRefs.forEach((item) => {
        changes[item.id] = {
          key: 'transform',
          value: { ...item.transform }
        }
      })
      this.selectionTransform = transform
      this.application.batchUpdateControlValue(changes)
    },
    /**
     * 處理批量縮放事件
     * 根據選中區域的縮放，同步更新所有選中控件的位置和尺寸
     * @param {Event} e - 事件對象
     */
    handleSelectionResize(e) {
      e.stopPropagation()
      const area = this.$refs.transformBox.transform
      this.componentRefs.forEach((item) => {
        item.transform.x = Math.round(area.x + area.width * item._xPercent)
        item.transform.y = Math.round(area.y + area.height * item._yPercent)
        item.transform.width = Math.round(area.width * item._wPercent)
        item.transform.height = Math.round(area.height * item._hPercent)
      })
      this.getSelectedComponentTransform()
    }
  },
  render() {
    const { x, y, width, height } = this.selectionArea
    return (
      <div data-plugin="selection" class="plugin-selection">
        <div
          style={`
            display:${width > 0 ? 'block' : 'none'};
            width:${width}px;
            height:${height}px;
            left:${x}px;
            top:${y}px`}
          class="selection-box"
        />
        {this.selectedComponents.map((item) => {
          return (
            <div
              class="selection-selected-component-bordered"
              style={`
                transform:rotate(${item.rotation}deg);
                left:${item.x}px;
                top:${item.y}px;
                width:${item.width}px;
                height:${item.height}px`}
              key={item.id}
            />
          )
        })}

        <DDrComponent
          ref="transformBox"
          data-component="true"
          rotatable={false}
          grid={this.grid}
          resizeHandler={this.selectionHandler}
          active={this.selectionActive}
          onDragstart={this.stopPropagation}
          onDrag={this.handleSelectionDrag}
          onDragend={this.handleSelectionDragEnd}
          onResizestart={this.stopPropagation}
          onResize={this.handleSelectionResize}
          onResizeend={this.handleSelectionDragEnd}
          value={this.selectionTransform}
        />
      </div>
    )
  }
}
</script>

<style lang="less" scoped>
.selection-box {
  border: 1px dashed blue;
  background: rgba(255, 255, 255, 0.2);
  display: none;
  position: absolute;
}

.selection-selected-component-bordered {
  border: 1px solid blue;
  position: absolute;
}
</style>
